import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, MapPin, ShieldCheck, Sparkles, TrendingUp } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { DEMO_PROPERTIES, DEMO_COMPS } from "../data/demo";
import { Demo } from "../components/Bits";

const aed = (n) => "AED " + Number(n).toLocaleString();

export default function PropertyDetail() {
  const { t } = useLang();
  const { id } = useParams();
  const nav = useNavigate();
  const p = DEMO_PROPERTIES.find((x) => x.id === id);

  if (!p) return (
    <div className="max-w-[900px] mx-auto px-5 py-24 text-center" data-testid="property-notfound">
      <p className="text-white/60">Property not found.</p>
      <Link to="/property" className="text-gold mt-3 inline-block">← {t("nav.property")}</Link>
    </div>
  );

  const psf = Math.round(p.price / p.size);
  return (
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-10" data-testid="property-detail-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-white/60 hover:text-gold text-sm mb-5"><ArrowLeft size={15} /> {t("common.back")}</button>

      <div className="relative rounded-3xl overflow-hidden h-[340px]">
        <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
        <div className="absolute top-4 right-4"><Demo /></div>
        <div className="absolute bottom-0 p-7">
          <div className="flex items-center gap-2 text-white/70 text-sm"><MapPin size={14} className="text-gold" /> {p.area}</div>
          <h1 className="font-display text-3xl sm:text-4xl text-white mt-1">{p.title}</h1>
          <div className="font-display text-2xl text-gold mt-1">{aed(p.price)}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-8">
        <div className="md:col-span-2 space-y-6">
          <div className="glass rounded-2xl p-6">
            <h2 className="font-display text-xl text-white mb-4">{t("property.detail_facts")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              {[["Bedrooms", p.bedrooms], ["Bathrooms", p.baths], ["Size", `${p.size} sqft`], ["AED/sqft", aed(psf)]].map(([k, v]) => (
                <div key={k}><div className="text-white/40 text-xs">{k}</div><div className="text-white mt-0.5">{v}</div></div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">{p.tags.map((tg) => <span key={tg} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-white/70">{tg}</span>)}</div>
            <div className="mt-4 text-xs text-white/40">{t("property.rera")}: <span className="text-white/60">DEMO-0000-0000</span> · {t("property.freshness")}: {p.freshness}</div>
          </div>

          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4"><TrendingUp size={16} className="text-gold" /><h2 className="font-display text-xl text-white">{t("property.detail_comps")}</h2><Demo /></div>
            <div className="space-y-2">
              {DEMO_COMPS.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white/70">{c.building} · {c.size} sqft</span>
                  <span className="text-white">{aed(c.price)} <span className="text-white/40 text-xs">· {c.date}</span></span>
                </div>
              ))}
            </div>
          </div>

          <div className="glass rounded-2xl p-6">
            <h2 className="font-display text-xl text-white mb-2">{t("property.detail_area")}</h2>
            <div className="rounded-xl h-40 bg-[repeating-linear-gradient(45deg,rgba(255,255,255,0.03),rgba(255,255,255,0.03)_12px,transparent_12px,transparent_24px)] border border-white/5 flex items-center justify-center text-white/40 text-sm">
              <MapPin size={16} className="text-gold mr-2" /> Map placeholder — {p.area}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-wider mb-2"><Sparkles size={12} /> {t("property.detail_why")}</div>
            <p className="text-white/60 text-sm">Strong AED/sqft vs recent comparable sales in {p.area}, with resale-friendly layout and amenities. (DEMO explanation.)</p>
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 text-white text-sm font-semibold"><ShieldCheck size={15} className="text-emerald-400" /> {t("property.detail_broker")}</div>
            <p className="text-white/50 text-xs mt-1">Verified brokerage connects on request (DEMO).</p>
            <button onClick={() => nav("/ask", { state: { prompt: `Request a viewing for ${p.title} in ${p.area}` } })} data-testid="request-viewing-btn"
              className="mt-4 w-full py-2.5 rounded-lg bg-gold text-ink font-semibold hover:brightness-110 transition-all duration-200">{t("common.request_viewing")}</button>
            <button onClick={() => nav("/ask", { state: { prompt: `Why is ${p.title} a good buy?` } })}
              className="mt-2 w-full py-2.5 rounded-lg border border-white/10 text-white/80 hover:border-gold/30 transition-colors duration-200">{t("common.why")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
